/**
 * One mission, from the aircraft to the debrief: fixed-step simulation, interpolated
 * rendering, input, HUD. Data flows one way each frame (06 §R3): input -> Command -> sim ->
 * state + events -> render, effects, HUD. `startMission` returns a handle so the game flow
 * can tear the mission down and hand over to the menus.
 */

import type { WebGLRenderer } from 'three';
import type { GameAudio } from '../audio';
import { drawnAt, groundFactor } from '../input/aimPick';
import { aimTargets, lampTargets } from '../input/aimTargets';
import { machineVolumes, type PropVolume, propVolumes, standingVolumes } from './aimVolumes';
import { type Bindings, keyLabel } from '../input/bindings';
import { Input } from '../input/input';
import { availableWeapons } from '../input/weaponCycle';
import type { MissionSummary } from '../meta/types';
import type { DeathCamera } from '../platform/photos/types';
import { knifeTarget } from '../sim/ranger';
import { type Command, DT, type Loadout, type MissionDef, type MissionResult, type SimEvent, type SimMods, type World } from '../sim/types';
import { createSim, hashWorld, NEUTRAL_COMMAND, restoreSim, stepSim } from '../sim/world';
import { FlyoverHud } from '../ui/flyover';
import { Hud } from '../ui/hud';
import { installDebugApi } from './debugApi';
import { saveFrame } from './frameSave';
import { DEFAULT_MISSION_SETTINGS, type MissionSettings } from './missionSettings';
import { DebugLayers } from '../render/debug/DebugLayers';
import { MissionView } from './missionView';
import { groundProjector } from './projector';
import { promptFor } from './prompts';
import type { Recording } from './replay';
import { quantise, ReplayRecorder } from './replayCodec';
import { type MissionFacts, newFacts, observe, photograph, summarise } from './summary';

/**
 * ROUND7 §5: the training course's coach. It watches every tick and draws its own panel over the HUD; the
 * mission loop only mounts it, feeds it, and takes it down.
 */
export interface MissionCoach {
  mount(root: HTMLElement): void;
  tick(world: World, events: readonly SimEvent[]): void;
  dispose(): void;
}

export interface MissionOptions {
  def: MissionDef;
  /** ROUND7 §5: training only. */
  coach?: MissionCoach;
  loadout?: Loadout;
  mods?: Partial<SimMods>;
  /** Start on the ground instead of in the aircraft. */
  skipInsertion: boolean;
  pitch: number;
  ppm: number;
  showHud: boolean;
  audio?: GameAudio;
  /** The shared key-binding store; the default one when absent (dev routes). */
  bindings?: Bindings;
  /** Comfort and control options from the menus (08 §4). */
  settings?: MissionSettings;
  /** Daily Drop (04 §11): pausing hides the playfield, so a pause cannot be used to plan. */
  hidePlayfieldOnPause?: boolean;
  /** The pause menu's way out, and what it is called. Without it the button restarts the page (dev routes). */
  quit?: { label: string; go(): void };
  /** A suspended mission (04 §10): the sim picks up exactly where it stopped. */
  resume?: Suspended;
  /** Play these commands, one per tick, instead of the player's; then check the world against `finalHash`. */
  playback?: { commands: Command[]; finalHash: string };
  /** PHOTOS §4: only where a death puts a name on the Memorial Wall (an operation, Classic). */
  camera?: DeathCamera;
  /**
   * Called once, a moment after the mission ends. When given, the in-mission debrief is
   * left to the caller (the menus); when absent the HUD shows its own and offers a restart.
   */
  onEnd?: (result: MissionResult, summary: MissionSummary) => void;
}

/** All a mission needs to be picked up again: both are plain JSON. */
export interface Suspended {
  world: World;
  facts: MissionFacts;
}

export interface MissionHandle {
  /** The live mission while it is undecided, for a suspend save; null once it has a result. */
  snapshot(): Suspended | null;
  /** Every command since tick 0, or null for a mission that was resumed or is itself a playback. */
  recording(): Recording | null;
  dispose(): void;
}

const END_DELAY_SECONDS = 1.6;

export function startMission(canvas: HTMLCanvasElement, renderer: WebGLRenderer, options: MissionOptions): MissionHandle {
  const { def, playback } = options;
  const simOptions: Parameters<typeof createSim>[1] = { skipInsertion: options.skipInsertion };
  if (options.loadout) simOptions.loadout = options.loadout;
  if (options.mods) simOptions.mods = options.mods;
  const sim = options.resume ? restoreSim(def, options.resume.world) : createSim(def, simOptions);
  const facts = options.resume?.facts ?? newFacts(options.mods?.luckyCharges ?? 0);
  // A replay must start at tick 0, so a resumed mission records nothing.
  const recorder = options.resume || playback ? null : new ReplayRecorder();
  const audio = options.audio;
  audio?.beginMission(def);

  const settings = options.settings ?? DEFAULT_MISSION_SETTINGS;
  const view = new MissionView(renderer, def, sim.world, options.ppm, options.pitch, settings);
  const { camera, pipeline } = view;
  // Debug layers (06 §8) cost nothing until one is switched on from `__game.layer(name, on)`.
  const debug = new DebugLayers(view.scene, view.palette, view.world3d.groundY);

  const input = options.bindings ? new Input(canvas, options.bindings) : new Input(canvas);
  // R6: the throw crosshair's ceiling is this ranger's own arm. The sim's resolved mods, not
  // `options.mods`, so a perk that reaches the world reaches the input that has to aim it.
  input.setOptions({
    runToggle: settings.runToggle, crawlToggle: settings.crawlToggle, sneakToggle: settings.sneakToggle,
    grenadeRange: sim.world.mods.grenadeRange,
  });
  // A resumed mission waits on the pause screen: nobody should be shot while finding the keyboard.
  let paused = options.resume !== undefined;
  const selfDebrief = options.onEnd === undefined;
  const hud = options.showHud
    ? new Hud(document.body, def, {
      onRestart: options.quit?.go ?? (() => location.reload()),
      onResume: () => { paused = false; },
      ...(options.quit ? { leaveLabel: options.quit.label } : {}),
      ...(options.bindings ? { bindings: options.bindings } : {}),
      hidePlayfieldOnPause: options.hidePlayfieldOnPause === true,
      view: settings.hud,
    })
    : null;
  hud?.setProjector(groundProjector(camera, pipeline));
  const interactKey = keyLabel(options.bindings?.keysFor('interact')[0] ?? 'KeyE');
  const flyHud = options.showHud ? new FlyoverHud(document.body, def) : null;
  if (paused) hud?.setPaused(true);
  const groundHudRoot = document.querySelector<HTMLElement>('.hud-root');
  const coach = options.coach ?? null;
  coach?.mount(document.body);
  canvas.style.cursor = 'crosshair';

  let accumulator = 0;
  let last = performance.now();
  let time = 0;
  let endedFor = 0;
  let reported = false;
  let running = true;
  let raf = 0;
  let verdict: string | null = null;
  const frameEvents: SimEvent[] = [];
  /** The map's destroyable props as the cursor can meet them: built on the first ground command. */
  let aimSolids: PropVolume[] | null = null;

  /** One fixed step of the simulation, with everything that must see each of its events. */
  function tick(cmd: Command): void {
    recorder?.push(cmd);
    const events = stepSim(sim, cmd);
    for (const e of events) {
      frameEvents.push(e);
      if (e.type === 'propDestroyed') view.destroyProp(e.id);
      if (e.type === 'propRemoved') view.removeProp(e.id);
      observe(facts, e, sim.world, def);
    }
    coach?.tick(sim.world, events);
  }

  /** This tick's command: the recording's on playback, otherwise the player's, quantised so it can be recorded exactly. */
  function nextCommand(): Command {
    if (playback) return playback.commands[sim.world.tick] ?? NEUTRAL_COMMAND;
    const r = sim.world.ranger;
    // In the air the classic scheme steers directly: no tap delay, no turn-before-step.
    input.setAirborne(sim.world.insertion.phase !== 'ground');
    // ROUND7 §2: the men the cursor can be on, so a cursor on one aims at his feet, not the ground behind him.
    // A searchlight's lamp too: drawn 3 m up its pole, a cursor on it aimed 3 m past the pole a round must strike.
    const ground = view.world3d.groundY;
    input.setAimTargets([...aimTargets(sim.world.enemies, ground), ...lampTargets(def, sim.world.props, ground)]);
    // And every other solid standing up - a truck, a tower, a wall - as tall as it is drawn (the aim audit).
    aimSolids ??= propVolumes(def, view.palette, ground);
    const machines = machineVolumes(sim.world.enemies, view.palette, ground);
    input.setAimVolumes([...standingVolumes(aimSolids, sim.world.props), ...machines]);
    return quantise(input.command(r.x, r.z, r.weapon, camera, pipeline.viewRect, availableWeapons(r)));
  }

  function frame(now: number): void {
    if (!running) return;
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;

    if (input.consumePause() && !sim.world.result) {
      paused = !paused;
      hud?.setPaused(paused);
    }

    frameEvents.length = 0;
    if (!paused) {
      time += dt;
      // The game-speed option slows the ground phase only (08 §4); ticks, and so replays, are unaffected.
      accumulator += dt * (sim.world.insertion.phase === 'ground' ? settings.gameSpeed : 1);
      while (accumulator >= DT) {
        accumulator -= DT;
        tick(nextCommand());
      }
    }
    present(paused ? 0 : dt, paused ? 1 : accumulator / DT);
    raf = requestAnimationFrame(frame);
  }

  /** Everything downstream of the sim for one frame: the view, audio, HUD, and the hand-over at the end. */
  function present(dt: number, alpha: number): void {
    const w = sim.world;
    debug.handle(frameEvents);
    debug.update(w, sim.statics, camera);
    view.present(w, frameEvents, time, dt, alpha);
    audio?.handle(frameEvents, w, camera.focus.x, camera.focus.z);
    audio?.update(w, dt, camera.focus.x, camera.focus.z);

    if (playback && !verdict && (w.result || w.tick >= playback.commands.length)) {
      verdict = hashWorld(w) === playback.finalHash ? 'REPLAY VERIFIED - IDENTICAL TO THE RECORDING' : 'REPLAY DIVERGED FROM THE RECORDING';
    }

    const onGround = w.insertion.phase === 'ground';
    const scale = pipeline.viewRect.width / 640;
    if (hud) {
      if (groundHudRoot) groundHudRoot.style.display = onGround && (selfDebrief || !w.result) ? '' : 'none';
      hud.layout(pipeline.viewRect, scale);
      // The ground HUD stays out of the way during the drop, and when the menus own the debrief.
      if (onGround && (selfDebrief || !w.result)) {
        hud.handle(frameEvents);
        hud.setOrdersHeld(input.mapHeld);
        hud.setPrompt(verdict ?? (playback ? 'REPLAY' : promptFor(sim, interactKey)));
        // ROUND7 §2: bracket the man a stab would take now, whatever is in his hands (Quick Knife works from any).
        const man = w.ranger.status === 'active' && !w.result ? knifeTarget(w, sim.statics, w.ranger.aim) : null;
        hud.setKnifeTarget(man && drawnAt(man.x, man.z, view.world3d.groundY(man.x, man.z), groundFactor(camera)));
        hud.update(w);
      }
    }
    if (flyHud) {
      flyHud.layout(pipeline.viewRect, scale);
      flyHud.update(w);
    }

    if (w.result && options.onEnd && !reported) {
      // The end-of-mission beat runs on wall time even if the last frame was a paused one.
      endedFor += dt > 0 ? dt : DT;
      if (endedFor >= END_DELAY_SECONDS) {
        reported = true;
        const summary = summarise(w, def, w.result, facts);
        // PHOTOS §4: the frame just drawn - the death pose settled, no HUD (that is DOM) - before anything covers it.
        photograph(summary, options.camera, () => pipeline.readPixels());
        options.onEnd(w.result, summary);
      }
    }
  }
  raf = requestAnimationFrame(frame);

  installDebugApi(sim, {
    // Steps and draws without waiting for animation frames, which a background tab never gets.
    advance(ticks, cmd) {
      frameEvents.length = 0;
      for (let i = 0; i < ticks; i++) tick(quantise({ ...NEUTRAL_COMMAND, ...cmd }));
      time += ticks * DT;
      present(Math.min(0.1, ticks * DT), 1);
    },
    play(ticks) {
      frameEvents.length = 0;
      for (let i = 0; i < ticks; i++) tick(nextCommand());
      time += ticks * DT;
      present(Math.min(0.1, ticks * DT), 1);
    },
    screenOf: (x, y, z) => groundProjector(camera, pipeline)(x, z - y * groundFactor(camera)),
    save: (name) => saveFrame(pipeline, view.scene, camera, name),
    verdict: () => verdict,
    layer: (name, on) => debug.setEnabled(name, on),
    look: (look) => pipeline.setOptions(look),
  });

  return {
    snapshot: () => (sim.world.result ? null : { world: sim.world, facts }),
    recording: () => (recorder
      ? { runs: recorder.runs, ticks: recorder.ticks, finalHash: hashWorld(sim.world), outcome: sim.world.result?.outcome ?? null }
      : null),
    dispose() {
      running = false;
      audio?.endMission();
      cancelAnimationFrame(raf);
      input.destroy();
      hud?.dispose();
      flyHud?.dispose();
      coach?.dispose();
      debug.dispose();
      view.dispose();
      canvas.style.cursor = '';
    },
  };
}
